import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import type { FramedIconSize } from './framed-icon.types';

export const stateClasses = {
  neutral: cn('bg-surface-accent text-text-primary newBrandDark:bg-surface-elevated'),
  error: cn('bg-validation-destructive-muted text-validation-destructive'),
  warning: cn('bg-validation-warning-muted text-validation-warning'),
  success: cn('bg-validation-success-muted text-validation-success'),
  pro: cn('bg-tier-pro-muted text-tier-pro-text'),
  practice: cn('bg-tier-practice-muted text-tier-practice-text'),
} as const;

/**
 * Entity-colour frames. Each pairs `bg-entity-{color}-muted` with
 * `text-entity-{color}-foreground` — the same token pair `Chip` and
 * `StatusIndicator` use, so a `color="blue"` frame matches a blue chip. Driven
 * by the `color` prop (mutually exclusive with `state`).
 */
export const colorClasses = {
  neutral: cn('bg-entity-neutral-muted text-entity-neutral-foreground'),
  red: cn('bg-entity-red-muted text-entity-red-foreground'),
  orange: cn('bg-entity-orange-muted text-entity-orange-foreground'),
  yellow: cn('bg-entity-yellow-muted text-entity-yellow-foreground'),
  green: cn('bg-entity-green-muted text-entity-green-foreground'),
  blue: cn('bg-entity-blue-muted text-entity-blue-foreground'),
  purple: cn('bg-entity-purple-muted text-entity-purple-foreground'),
  pink: cn('bg-entity-pink-muted text-entity-pink-foreground'),
} as const;

export const sizeClasses = {
  xs: cn('size-6 rounded-sm p-1.5'),
  sm: cn('size-8 rounded-md p-2'),
  md: cn('size-12 rounded-lg p-3'),
  lg: cn('size-14 rounded-lg p-4'),
  xl: cn('size-20 rounded-xl p-6'),
} as const;

/**
 * Pixel size applied to the inner icon for each frame size. Mirrors the
 * Figma component's inner-icon sizing.
 */
export const iconSizeMap: Record<FramedIconSize, number> = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 24,
  xl: 32,
};

export const framedIconVariants = cva(
  cn('inline-flex shrink-0 items-center justify-center'),
  {
    variants: {
      size: sizeClasses,
      state: stateClasses,
      color: colorClasses,
    },
    // Only `size` defaults — `state`/`color` are mutually exclusive and resolved
    // by the component, which passes exactly one. Defaulting `state` here would
    // re-apply the neutral fill whenever a `color`-only call leaves `state`
    // undefined (cva falls back to defaults on `undefined`).
    defaultVariants: {
      size: 'md',
    },
  }
);
